import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faHistory, faTimes, faUser, faCalendarAlt, faInfoCircle, faClock, faChevronUp, faChevronDown, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { KnowledgeBaseService, KnowledgeBaseHistoryEntry } from '../../services/knowledge-base.service';
import { ConfigurationsService } from '../../services/configurations.service';
import { AlertService } from '../alert-service/alert-service';
import { DateFormatterService } from '../../services/date-formatter.service';
import { ButtonComponent } from '../button/button.component';

const KNOWN_SPLIT_METHODS = ['lines', 'pages', 'tables', 'summary_based'];

export interface KnowledgeBaseArchive {
  id: string;
  name: string;
}

@Component({
  selector: 'app-modal-knowledge-base-history',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, TranslateModule, ButtonComponent],
  templateUrl: './modal-knowledge-base-history.component.html',
  styleUrls: ['./modal-knowledge-base-history.component.scss'],
})
export class ModalKnowledgeBaseHistoryComponent implements OnInit, OnChanges {
  @Input() show = false;
  @Input() archive: KnowledgeBaseArchive | null = null;
  @Output() close = new EventEmitter<void>();

  faHistory = faHistory;
  faTimes = faTimes;
  faUser = faUser;
  faCalendarAlt = faCalendarAlt;
  faInfoCircle = faInfoCircle;
  faClock = faClock;
  faChevronUp = faChevronUp;
  faChevronDown = faChevronDown;
  faCheckCircle = faCheckCircle;

  loading = false;
  history: KnowledgeBaseHistoryEntry[] = [];
  selectedEntry: KnowledgeBaseHistoryEntry | null = null;
  private delfosServerLabelById = new Map<string, string>();

  constructor(
    private knowledgeBaseService: KnowledgeBaseService,
    private configurationsService: ConfigurationsService,
    private alert: AlertService,
    private translate: TranslateService,
    public dateFormatter: DateFormatterService,
  ) {}

  ngOnInit(): void {
    this.configurationsService.listAllowedForMe().subscribe({
      next: res => {
        const delfosServerConfigs = (res.configurations || []).filter(c => c.type === 'delfos_server');
        this.delfosServerLabelById = new Map(delfosServerConfigs.map(c => [c.id, c.display_label || c.key]));
      },
      error: () => { this.delfosServerLabelById = new Map(); },
    });
  }

  splitLabel(method: string): string {
    return KNOWN_SPLIT_METHODS.includes(method)
      ? this.translate.instant('KNOWLEDGE_BASE.SPLIT.' + method)
      : method;
  }

  delfosServerLabel(entry: KnowledgeBaseHistoryEntry): string {
    if (!entry.delfos_server_config_id) return '—';
    return this.delfosServerLabelById.get(entry.delfos_server_config_id) || '—';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['show']?.currentValue && this.archive?.id) {
      this.loadHistory();
    }
  }

  loadHistory(): void {
    if (!this.archive?.id) return;
    this.loading = true;
    this.knowledgeBaseService.getHistory(this.archive.id).subscribe({
      next: (data) => {
        this.history = data;
        this.loading = false;
      },
      error: (err) => {
        this.alert.showError(err);
        this.loading = false;
      },
    });
  }

  onClose(): void {
    this.close.emit();
    this.selectedEntry = null;
  }

  toggleDetails(entry: KnowledgeBaseHistoryEntry): void {
    this.selectedEntry = this.selectedEntry?.id === entry.id ? null : entry;
  }

  formatDate(dateStr: string): string {
    return this.dateFormatter.formatDate(dateStr);
  }

  formatTime(dateStr: string): string {
    return this.dateFormatter.formatTime(dateStr);
  }
}
